import type { ComponentType, ReactNode } from 'react';
import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';

import BrandThemeProvider from '@rover/kibble/themes/BrandThemeProvider';
import { RSDKProviders } from '@rover/rsdk/src/';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import ObservabilityMetadata from '@rover/rsdk/src/modules/ObservabilityMetadata';
import { RoverStatsigProvider } from '@rover/rsdk/src/modules/Statsig';
import RoverBrandProvider from '@rover/shared/js/providers/RoverBrandContextProvider';
import getRoverBrand from '@rover/utilities/getRoverBrand';
import inject from '@rover/utilities/injector';

export default function renderRootElement<Props>(
  Component: ComponentType<Props> | ReactNode,
  elementSelector: string,
  props?: Props
): Root {
  const locale = inject('Rover.context.language', undefined);
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const rootElement = document.querySelector(elementSelector);
  const brand = getRoverBrand();

  if (!rootElement) {
    throw new Error(`rootElement not found with elementSelector ${elementSelector}`);
  }

  // Get experiment user attributes from server context and convert to camelCase
  // Preserve snake_case for rover_cid and environment_id keys as they must match Statsig console configuration
  const experimentUserAttributesSnakeCase = inject(
    'Rover.context.experiment_user_attributes',
    undefined
  );
  const initialUserAttributes = experimentUserAttributesSnakeCase
    ? camelize(experimentUserAttributesSnakeCase, true, ['rover_cid', 'environment_id'])
    : undefined;

  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  ObservabilityMetadata.set('currentView', window.location.pathname);
  ObservabilityMetadata.set('locale', locale);
  try {
    ObservabilityMetadata.set('userOPK', inject('Rover.context.person.opk', undefined));
  } catch (e) {
    // this can throw when unauthed, but we don't care
  }

  // We only want to render ReactQueryDevTools once per page, even on a Django page where renderRootElement
  // is being called multiple times for different included React components
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const reactQueryDevToolsExists = document.querySelector('#ReactQueryDevtoolsPanel');

  // start the react app
  const root = createRoot(rootElement);
  // flushSync forces sync rendering of the app https://stackoverflow.com/a/71983073
  // important in django/react apps where we may try to talk to React from other JS after calling renderRootElement
  flushSync(() => {
    root.render(
      <RSDKProviders shouldRenderReactQueryDevTools={!reactQueryDevToolsExists} locale={locale}>
        <RoverBrandProvider brand={brand}>
          <BrandThemeProvider brand={brand}>
            <RoverStatsigProvider initialUserAttributes={initialUserAttributes}>
              {/* @ts-expect-error */}
              <Component {...props} />
            </RoverStatsigProvider>
          </BrandThemeProvider>
        </RoverBrandProvider>
      </RSDKProviders>
    );
  });

  // We set min-height on the root container to minimize CLS. Once rendered, we can remove it
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  const element = document.querySelector(elementSelector);
  if (element?.getAttribute('data-should-remove-style') === 'true') {
    element?.removeAttribute('style');
  }
  return root;
}
